import { Box, Dialog, Divider, IconButton, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import Translate from "../../utils/Translate";
import StUnitField from "../orders/StUnitField";
import InOrderGridItem from "./InOrderGridItem";
import PartChildChip from "./PartChildChip";
import { useCallback, useState } from "react";
import { AddCircle, AddCircleOutline, CallSplit, Edit } from "@mui/icons-material";
import StUnitForm from "./StUnitForm";
import { useNavigate } from "react-router-dom";
import SplitStUnitForm from "./SplitStUnitForm";
import { useSnackbar } from "notistack";
import { formatCurrency, formatNumberToLocale } from "../../utils/formatNumber";

export default function PartDetailsPanel({ part, stUnits, inOrders, needInOrder, onShowInOrder, reloadData }) {
  const [showAddStUnit, setShowAddStUnit] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  function showDoneMessage() {
    const successMessage = enqueueSnackbar(Translate.get("Ready"), {
      variant: "success",
      autoHideDuration: 6000,
      onClick: () => closeSnackbar(successMessage),
    });
  }

  const handleSaveStUnit = useCallback(async () => {
    showDoneMessage();
    if (showAddStUnit) {
      setShowAddStUnit(false);
    } else {
      window.history.back();
    }
    await reloadData();
  }, [showAddStUnit]);

  function getStUnitDotMenuActions(stUnit) {
    return [
      {
        icon: <Edit />,
        text: Translate.get("EditStUnitLabel"),
        customClass: "stUnitDialog",
        component: <StUnitForm stUnit={stUnit} onClose={() => window.history.back()} onSave={handleSaveStUnit} />,
      },
      {
        icon: <CallSplit />,
        disabled: stUnit.isUnlimitedQty,
        text: Translate.get("SplitStUnit"),
        customClass: "stUnitDialog",
        component: (
          <SplitStUnitForm
            stUnit={stUnit}
            onClose={() => window.history.back()}
            onSplit={async () => {
              showDoneMessage();
              window.history.back();
              await reloadData();
            }}
          />
        ),
      },
    ];
  }

  return (
    <>
      {showAddStUnit && (
        <Dialog open className="stUnitDialog" onClose={() => setShowAddStUnit(false)}>
          <StUnitForm
            stUnit={{ partId: part.partId }}
            onClose={() => setShowAddStUnit(false)}
            onSave={handleSaveStUnit}
          />
        </Dialog>
      )}
      <Grid container padding={2} spacing={5} direction="column" className="partDetailsPanel">
        {(needInOrder || (inOrders && inOrders.length > 0)) && (
          <Grid container spacing={1} direction="column" size={12}>
            <Grid >
              <h4>{Translate.get("InOrdersLabel")}</h4>
            </Grid>
            {inOrders && inOrders.length > 0 && (
              <Grid container spacing={1}>
                {inOrders.map((x, index) => (
                  <Grid container key={x.inOrderId}>
                    <InOrderGridItem inOrder={x} part={part} onShowInOrder={onShowInOrder} reloadData={reloadData} />
                  </Grid>
                ))}
              </Grid>
            )}
            {(!inOrders || inOrders.length === 0) && <Grid >-</Grid>}
          </Grid>
        )}

        <Grid container spacing={2} direction="column">
          <Grid >
            <Stack direction="row" spacing={3} alignItems="center">
              <Stack direction="row" alignItems="center">
                <h4>{Translate.get("StorageInfoLabel")}</h4>
                <IconButton onClick={() => setShowAddStUnit(true)}>
                  <AddCircleOutline />
                </IconButton>
              </Stack>
              {stUnits && stUnits.length > 0 && <StUnitHeaderDetails stUnits={stUnits} />}
            </Stack>
          </Grid>

          {stUnits && stUnits.length > 0 && (
            <Grid container spacing={1}>
              {stUnits.map((st) => (
                <Grid key={st.stUnitId}>
                  <StUnitField {...st} dotMenuActions={getStUnitDotMenuActions(st)} />
                </Grid>
              ))}
            </Grid>
          )}
          {(!stUnits || stUnits.length === 0) && <Grid >-</Grid>}
        </Grid>

        {part.children && part.children.length > 0 && (
          <Grid container spacing={2} direction="column">
            <Grid >
              <h4>{Translate.get("MultiPartLabel")}</h4>
            </Grid>

            <Grid container spacing={1}>
              {part.children.map((child, index) => (
                <Grid key={index}>
                  <PartChildChip {...child} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}

function StUnitHeaderDetails({ stUnits }) {
  const [hasUnlimitedQty] = useState(stUnits && stUnits.findIndex((st) => !!st.isUnlimitedQty) !== -1);
  const [totalQtyNonBlocked] = useState(
    stUnits ? stUnits.filter((st) => !st.blocked).reduce((acc, st) => acc + st.qty, 0) : 0
  );
  const [totalQtyBlocked] = useState(
    stUnits ? stUnits.filter((st) => !!st.blocked).reduce((acc, st) => acc + st.qty, 0) : 0
  );
  const [totalValue] = useState(stUnits ? stUnits.reduce((acc, st) => acc + (st.pcsPrice ?? 0) * st.qty, 0) : 0);

  return (
    <>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box>{`${Translate.get("StUnitTotalValueLabel")}:`}</Box>
          <Box className="iconbox">{hasUnlimitedQty ? "-" : `${formatCurrency(Math.trunc(totalValue))} kr`}</Box>
        </Stack>
      </Typography>

      {hasUnlimitedQty && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>{`${Translate.get("Quantity")}: ∞`}</Typography>
      )}

      {!hasUnlimitedQty && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box>{`${Translate.get("Quantity")}:`}</Box>
            <Box className="iconbox success">{formatNumberToLocale(totalQtyNonBlocked)}</Box>
            <Box className="iconbox warning">{formatNumberToLocale(totalQtyBlocked)}</Box>
          </Stack>
        </Typography>
      )}
    </>
  );
}
